import { UrlObject } from 'url';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useCoreClient, useGetPaginationState } from '@hooks';
import {
  donationsReportState,
  loadingState,
  notificationErrorState,
  OrganizationReportState,
  overviewReportState,
  qualifiedOrdersReportState,
} from '@states';
import { useRecoilState, useRecoilValue } from 'recoil';
import { performanceSearchState } from 'src/states/performance';
import { isArray } from 'lodash';
import { removeUndefinedKeys } from '@utils/transform';
import { PerformanceQueryParams } from 'src/types';

export const useFetchDonationsReport = () => {
  const [, setDonationsReport] = useRecoilState(donationsReportState);
  const performanceSearch = useRecoilValue(performanceSearchState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const [, setLoading] = useRecoilState(loadingState);
  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setTotalPages,
    totalPages,
  } = useGetPaginationState();

  const [previousPage, setPreviousPage] = useState(currentPage);
  const [previousPageSize, setPreviousPageSize] = useState(pageSize);
  const [refetch, setRefetch] = useState(false);

  const coreClient = useCoreClient();

  useEffect(() => {
    const fetchDonationsHistoryReport = async () => {
      setLoading(true);

      const { data, errors } = await coreClient().Donations.search({
        page: currentPage,
        pageSize,
        type: performanceSearch?.donationsType,
        ...performanceSearch,
      });

      if (errors) {
        setNotificationError(errors);
      } else {
        setDonationsReport(data.donations);
        setPreviousPage(currentPage);
        setPreviousPageSize(pageSize);
        setTotalPages(data.meta.totalPages);
      }

      setLoading(false);
    };

    if (
      refetch ||
      currentPage !== previousPage ||
      pageSize !== previousPageSize ||
      performanceSearch
    ) {
      fetchDonationsHistoryReport();
    }
  }, [
    pageSize,
    currentPage,
    refetch,
    performanceSearch,
    previousPage,
    previousPageSize,
    setLoading,
    setNotificationError,
    setDonationsReport,
    setTotalPages,
    coreClient,
  ]);

  return {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setRefetch,
    totalPages,
  };
};

export const useFetchOverviewReport = () => {
  const [overviewReport, setOverviewReport] =
    useRecoilState(overviewReportState);
  const performanceSearch = useRecoilValue(performanceSearchState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const [, setLoading] = useRecoilState(loadingState);
  const coreClient = useCoreClient();

  useEffect(() => {
    const fetchOverviewReport = async () => {
      setLoading(true);
      const { data, errors } = await coreClient().Reports.overview({
        campaignGids: performanceSearch?.campaignGids,
        endedAt: performanceSearch?.endedAt,
        startedAt: performanceSearch?.startedAt,
      });
      if (errors) {
        setNotificationError(errors);
      } else {
        setOverviewReport(data);
      }

      setLoading(false);
    };

    fetchOverviewReport();
  }, [
    setLoading,
    performanceSearch,
    setNotificationError,
    setOverviewReport,
    coreClient,
  ]);

  return { overviewReport };
};

export const useFetchQualifiedOrdersReport = () => {
  const performanceSearch = useRecoilValue(performanceSearchState);
  const [qualifiedOrdersReport, setQualifiedOrdersReport] = useRecoilState(
    qualifiedOrdersReportState,
  );
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const [, setLoading] = useRecoilState(loadingState);
  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setTotalPages,
    totalPages,
  } = useGetPaginationState();

  const coreClient = useCoreClient();

  useEffect(() => {
    const fetchQualifiedOrdersReport = async () => {
      setLoading(true);
      const { data, errors } = await coreClient().Orders.search({
        campaignGids: performanceSearch?.campaignGids,
        endedAt: performanceSearch?.endedAt,
        page: currentPage,
        pageSize,
        qualifying: true,
        startedAt: performanceSearch?.startedAt,
      });

      if (errors) {
        setNotificationError(errors);
      } else {
        setQualifiedOrdersReport(data.orders);
        setTotalPages(data.meta.totalPages);
      }

      setLoading(false);
    };

    fetchQualifiedOrdersReport();
  }, [
    setLoading,
    pageSize,
    currentPage,
    performanceSearch,
    setNotificationError,
    setQualifiedOrdersReport,
    setTotalPages,
    coreClient,
  ]);

  return {
    currentPage,
    pageSize,
    qualifiedOrdersReport,
    setCurrentPage,
    setPageSize,
    totalPages,
  };
};

export const useFetchOrganizationReport = () => {
  const [OrganizationReport, setOrganizationReport] = useRecoilState(
    OrganizationReportState,
  );
  const performanceSearch = useRecoilValue(performanceSearchState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const [, setLoading] = useRecoilState(loadingState);
  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setTotalPages,
    totalPages,
  } = useGetPaginationState();

  const coreClient = useCoreClient();

  useEffect(() => {
    const fetchOrganizationReport = async () => {
      setLoading(true);

      const { data, errors } = await coreClient().Reports.organizations({
        campaignGids: performanceSearch?.campaignGids,
        donationsType: performanceSearch?.donationsType,
        endedAt: performanceSearch?.endedAt,
        page: currentPage,
        pageSize,
        startedAt: performanceSearch?.startedAt,
      });

      if (errors) {
        setNotificationError(errors);
      } else {
        setOrganizationReport(data.organizations);
        setTotalPages(data.meta.totalPages);
      }

      setLoading(false);
    };

    fetchOrganizationReport();
  }, [
    setLoading,
    pageSize,
    currentPage,
    performanceSearch,
    setNotificationError,
    setOrganizationReport,
    setTotalPages,
    coreClient,
  ]);

  return {
    OrganizationReport,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    totalPages,
  };
};

type PerformanceSearchUrl = Omit<UrlObject, 'query'> & {
  query: PerformanceQueryParams;
};

export const usePerformanceSearch = () => {
  const router = useRouter();
  const performanceQueryParams = router.query as PerformanceQueryParams;
  const [performanceSearch, setPerformanceSearch] = useRecoilState(
    performanceSearchState,
  );

  useEffect(() => {
    const {
      campaignGids,
      donationGid,
      donationsType,
      endedAt,
      invoiceKey,
      organizationGid,
      startedAt,
    } = performanceQueryParams;

    setPerformanceSearch(
      removeUndefinedKeys({
        ...(campaignGids && {
          campaignGids: isArray(campaignGids) ? campaignGids : [campaignGids],
        }),
        donationGid,
        donationsType,
        endedAt,
        invoiceKey,
        organizationGid,
        startedAt,
      }),
    );
    return () => {
      setPerformanceSearch(undefined);
    };
  }, [
    router.query,
    router.asPath,
    performanceQueryParams,
    setPerformanceSearch,
  ]);

  const pushRoute = (url: PerformanceSearchUrl) => {
    const pathname = url.pathname ?? router.pathname;
    const query = removeUndefinedKeys({
      ...router.query,
      ...url.query,
    });
    router.push({
      ...url,
      pathname,
      query,
    } as UrlObject);
  };

  const replaceRoute = (url: PerformanceSearchUrl) => {
    const pathname = url.pathname ?? router.pathname;
    const query = removeUndefinedKeys(url.query);
    router.push({
      ...url,
      pathname,
      query,
    } as UrlObject);
  };

  return { performanceSearch, pushRoute, replaceRoute, setPerformanceSearch };
};
